import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, of, Subject, Subscription, switchMap } from 'rxjs';

import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

import { RescheduleProposalsService } from '../data-access/reschedule-proposals.service';
import {
  RescheduleProposalItem,
  RescheduleProposalPage,
  RescheduleProposalSort,
  RescheduleProposalStatus,
} from '../data-access/reschedule-proposals.models';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';

import { RescheduleProposalsTableComponent } from '../components/reschedule-proposals-table.component';
import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  ErrorBlockComponent,
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  ModalKeyboardDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';
import { UserProfilePageComponent } from '../../users/pages/user-profile-page.component';
import { AppointmentQuickViewModalComponent } from '../../appointments/components/appointment-quick-view-modal.component';
import { ClientsAdminService } from '../../clients/data-access/clients-admin.service';
import { ClientAdminItem } from '../../clients/data-access/clients-admin.models';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { UsersService } from '../../users/data-access/users.service';
import { UserItem } from '../../users/data-access/users.models';

@Component({
  selector: 'app-reschedule-proposals-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    CompanyContextRequiredComponent,
    PageShellComponent,
    PageContextGuardComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    ListFilterShortcutDirective,
    ModalKeyboardDirective,
    PageActionModalComponent,
    PagedFeedComponent,
    TranslatePipe,
    RescheduleProposalsTableComponent,
    UserProfilePageComponent,
    AppointmentQuickViewModalComponent
  ],
  template: `
    <lib-page-shell
      libListFilterShortcut
      [libListFilterShortcut]="!companyContextMissing"
      (libListFilterShortcutOpen)="openFiltersModal()"
    >
      <app-page-header
        title="Reschedule Proposals"
        subtitle="Appointments affected by downtime"
      />

      <lib-page-context-guard [blocked]="companyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!companyContextMissing) {
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }

        <lib-paged-feed
          [items]="items"
          [collectionTemplate]="proposalsTableTemplate"
          mode="manual"
          [hasMore]="hasMoreItems"
          [loading]="isLoading"
          [loadingMore]="isLoadingMore"
          [errorText]="errorText ? ((errorText | t) ?? '') : null"
          [loadingText]="'Loading proposals...' | t"
          [emptyText]="'No proposals found for current filters.' | t"
          [retryText]="'Retry' | t"
          [loadMoreText]="'Load more' | t"
          [loadingMoreText]="'Loading more...' | t"
          (loadMore)="loadMore()"
          (retry)="retry()"
        />

        <ng-template #proposalsTableTemplate let-feedItems>
          <app-reschedule-proposals-table
            [items]="feedItems"
            [canOpenClient]="canOpenClient"
            [canOpenAppointment]="true"
            [busyId]="busyId"
            [selectedId]="selectedItem?.id ?? null"
            (rowClicked)="select($event)"
            (clientOpenClicked)="openClient($event)"
            (appointmentOpenClicked)="openAppointmentQuickView($event)"
            (acceptClicked)="accept($event)"
            (rejectClicked)="reject($event)"
          />
        </ng-template>

        @if (isClientProfileModalOpen && selectedClientProfileId) {
          <div class="reschedule-proposals-page__modal-backdrop" (click)="closeClientProfileModal()">
            <div
              class="reschedule-proposals-page__modal"
              (click)="$event.stopPropagation()"
              [libModalKeyboard]="true"
              [libModalKeyboardCanSubmit]="false"
              (libModalKeyboardCancel)="closeClientProfileModal()"
            >
              <div class="reschedule-proposals-page__modal-head">
                <div>
                  <h3 class="reschedule-proposals-page__modal-title">{{ 'Client Profile' | t }}</h3>
                  <p class="reschedule-proposals-page__modal-subtitle">
                    {{ 'CRM client context without leaving the current reschedule queue.' | t }}
                  </p>
                </div>

                <button
                  type="button"
                  class="reschedule-proposals-page__modal-close"
                  (click)="closeClientProfileModal()"
                  aria-label="Close client profile modal"
                >
                  ×
                </button>
              </div>

              <app-user-profile-page
                [clientIdOverride]="selectedClientProfileId"
                presentationMode="quick-view"
              />
            </div>
          </div>
        }

        @if (isAppointmentQuickViewOpen && selectedAppointmentId) {
          <app-appointment-quick-view-modal
            [isOpen]="true"
            [appointmentId]="selectedAppointmentId"
            title="Appointment Quick View"
            (close)="closeAppointmentQuickView()"
          />
        }

        <lib-page-action-modal
          [isOpen]="isFiltersModalOpen"
          title="Filters & Sort"
          subtitle="Narrow reschedule proposals by appointment context."
          width="680px"
          [canSubmit]="true"
          (close)="closeFiltersModal()"
          (submit)="applyFilters()"
        >
          <div class="reschedule-proposals-page__filters">
            <label class="reschedule-proposals-page__filter">
              <span>{{ 'Clinic' | t }}</span>
              <select class="reschedule-proposals-page__control" [value]="draftClinicId" (change)="onDraftClinicChange($event)">
                <option value="">{{ 'All' | t }}</option>
                @for (clinic of clinicOptions; track clinic.clinicId) {
                  <option [value]="clinic.clinicId">{{ clinic.clinicName }}</option>
                }
              </select>
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'Client' | t }}</span>
              <input
                class="reschedule-proposals-page__control"
                [value]="draftClientSearchText"
                list="reschedule-client-options"
                placeholder="Search client"
                data-modal-primary-focus="true"
                (input)="onClientSearchInput($event)"
                (change)="onClientSearchCommitted($event)"
                (keyup.enter)="onClientSearchCommitted($event)"
              />
              <datalist id="reschedule-client-options">
                @for (client of clientOptions; track client.id) {
                  <option [value]="clientOptionLabel(client)"></option>
                }
              </datalist>
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'Specialist' | t }}</span>
              <select class="reschedule-proposals-page__control" [value]="draftSpecialistUserId" (change)="onDraftSpecialistChange($event)">
                <option value="">{{ 'All' | t }}</option>
                @for (specialist of specialistOptions; track specialist.id) {
                  <option [value]="specialist.id">{{ specialist.fullName }}</option>
                }
              </select>
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'Procedure' | t }}</span>
              <select class="reschedule-proposals-page__control" [value]="draftProcedureTypeId" (change)="onDraftProcedureTypeChange($event)">
                <option value="">{{ 'All' | t }}</option>
                @for (procedureType of procedureTypeOptions; track procedureType.id) {
                  <option [value]="procedureType.id">{{ procedureType.name }}</option>
                }
              </select>
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'Device' | t }}</span>
              <select class="reschedule-proposals-page__control" [value]="draftDeviceId" (change)="onDraftDeviceChange($event)">
                <option value="">{{ 'All' | t }}</option>
                @for (device of deviceOptions; track device.id) {
                  <option [value]="device.id">{{ deviceLabel(device) }}</option>
                }
              </select>
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'common.status' | t }}</span>
              <select class="reschedule-proposals-page__control" [value]="draftStatus" (change)="onDraftStatusChange($event)">
                <option value="">{{ 'All' | t }}</option>
                <option value="pending_client_confirmation">{{ 'status.pendingClientConfirmation' | t }}</option>
                <option value="accepted">{{ 'status.accepted' | t }}</option>
                <option value="rejected">{{ 'status.rejected' | t }}</option>
                <option value="expired">{{ 'status.expired' | t }}</option>
                <option value="revoked">{{ 'status.revoked' | t }}</option>
              </select>
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'Reason' | t }}</span>
              <input
                class="reschedule-proposals-page__control"
                [value]="draftReason"
                placeholder="Search reason"
                (input)="onDraftReasonChange($event)"
              />
            </label>

            <label class="reschedule-proposals-page__filter">
              <span>{{ 'appointments.filters.sort' | t }}</span>
              <select class="reschedule-proposals-page__control" [value]="draftSort" (change)="onDraftSortChange($event)">
                <option value="createdAtDesc">{{ 'Newest created' | t }}</option>
                <option value="createdAtAsc">{{ 'Oldest created' | t }}</option>
                <option value="updatedAtDesc">{{ 'Newest updated' | t }}</option>
                <option value="updatedAtAsc">{{ 'Oldest updated' | t }}</option>
                <option value="newStartAtAsc">{{ 'New slot earliest' | t }}</option>
                <option value="newStartAtDesc">{{ 'New slot latest' | t }}</option>
              </select>
            </label>

            <label class="reschedule-proposals-page__checkbox-filter">
              <input
                type="checkbox"
                [checked]="draftPendingFirst"
                (change)="onDraftPendingFirstChange($event)"
              />
              <span>{{ 'Pending first' | t }}</span>
            </label>
          </div>

          <div class="reschedule-proposals-page__modal-actions">
            <button type="button" class="reschedule-proposals-page__secondary-button" (click)="resetFilters()">
              {{ 'Reset' | t }}
            </button>
            <button type="button" class="reschedule-proposals-page__primary-button" (click)="applyFilters()">
              {{ 'Apply' | t }}
            </button>
          </div>
        </lib-page-action-modal>

        <ng-template #pageActionsMenu let-close="close">
          <button type="button" role="menuitem" class="action-menu__item" (click)="openFiltersModal(); close()">
            {{ 'Filters & Sort' | t }} (Ctrl+F)
          </button>
          <button type="button" role="menuitem" class="action-menu__item" (click)="refresh(); close()">
            {{ 'Refresh' | t }}
          </button>
        </ng-template>

        <lib-floating-action-fab
          [menuTemplate]="pageActionsMenu"
          buttonAriaLabel="Open reschedule proposal actions"
          [disabled]="companyContextMissing"
        />
      }
    </lib-page-shell>
  `,
  styles: [`
    .reschedule-proposals-page__modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.56);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      z-index: 70;
      backdrop-filter: blur(6px);
    }

    .reschedule-proposals-page__modal {
      width: min(980px, 100%);
      max-height: min(90vh, 980px);
      overflow: auto;
      border-radius: 20px;
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96));
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
      padding: 22px;
      display: grid;
      gap: 16px;
    }

    .reschedule-proposals-page__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }

    .reschedule-proposals-page__modal-title {
      margin: 0;
      font-size: 28px;
      line-height: 1.1;
      color: #111827;
    }

    .reschedule-proposals-page__modal-subtitle {
      margin: 6px 0 0;
      color: #64748b;
      font-size: 14px;
      line-height: 1.5;
    }

    .reschedule-proposals-page__modal-close {
      width: 42px;
      height: 42px;
      border-radius: 999px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #475569;
      cursor: pointer;
      font-size: 26px;
      line-height: 1;
      flex-shrink: 0;
    }

    .reschedule-proposals-page__filters {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      min-width: 0;
    }

    .reschedule-proposals-page__filter {
      display: grid;
      gap: 6px;
      color: #475569;
      font-size: 12px;
      font-weight: 700;
      min-width: 0;
    }

    .reschedule-proposals-page__checkbox-filter {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #475569;
      font-size: 13px;
      font-weight: 700;
    }

    .reschedule-proposals-page__control {
      width: 100%;
      min-width: 0;
      height: 38px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #ffffff;
      color: #0f172a;
      padding: 0 10px;
      font: inherit;
      font-weight: 500;
      box-sizing: border-box;
    }

    .reschedule-proposals-page__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .reschedule-proposals-page__primary-button,
    .reschedule-proposals-page__secondary-button {
      height: 38px;
      border-radius: 8px;
      padding: 0 14px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 700;
    }

    .reschedule-proposals-page__primary-button {
      border: 1px solid #2563eb;
      background: #2563eb;
      color: #ffffff;
    }

    .reschedule-proposals-page__secondary-button {
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #334155;
    }

    @media (max-width: 720px) {
      .reschedule-proposals-page__modal-backdrop {
        padding: 12px;
        align-items: stretch;
      }

      .reschedule-proposals-page__modal {
        width: 100%;
        max-height: none;
        border-radius: 18px;
        padding: 18px;
      }

      .reschedule-proposals-page__modal-title {
        font-size: 24px;
      }

      .reschedule-proposals-page__filters {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class RescheduleProposalsPageComponent implements OnInit, OnDestroy
{
  private static readonly PAGE_SIZE = 20;
  private static readonly CLIENT_SEARCH_LIMIT = 10;

  private readonly service = inject(RescheduleProposalsService);
  private readonly clientsAdminService = inject(ClientsAdminService);
  private readonly settingsService = inject(SettingsService);
  private readonly devicesService = inject(DevicesService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly usersService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();
  private readonly clientSearchTerms = new Subject<string>();
  private filterOptionsLoadedForCompanyId: string | null = null;

  protected items: RescheduleProposalItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected busyId: string | null = null;
  protected selectedItem: RescheduleProposalItem | null = null;
  protected canOpenClient = false;
  protected isSystemAdmin = false;
  protected isClientProfileModalOpen = false;
  protected isAppointmentQuickViewOpen = false;
  protected isFiltersModalOpen = false;
  protected selectedClientProfileId: string | null = null;
  protected selectedAppointmentId: string | null = null;
  protected nextCursor: string | null = null;
  protected selectedClinicId = '';
  protected selectedClientId = '';
  protected selectedSpecialistUserId = '';
  protected selectedProcedureTypeId = '';
  protected selectedDeviceId = '';
  protected selectedStatus: '' | RescheduleProposalStatus = '';
  protected selectedReason = '';
  protected selectedSort: RescheduleProposalSort = 'createdAtDesc';
  protected selectedPendingFirst = true;
  protected draftClinicId = '';
  protected draftClientId = '';
  protected draftClientSearchText = '';
  protected draftSpecialistUserId = '';
  protected draftProcedureTypeId = '';
  protected draftDeviceId = '';
  protected draftStatus: '' | RescheduleProposalStatus = '';
  protected draftReason = '';
  protected draftSort: RescheduleProposalSort = 'createdAtDesc';
  protected draftPendingFirst = true;
  protected clinicOptions: ClinicOperationalSettingsDto[] = [];
  protected clientOptions: ClientAdminItem[] = [];
  protected deviceOptions: DeviceItem[] = [];
  protected procedureTypeOptions: ProcedureTypeItem[] = [];
  protected specialistOptions: UserItem[] = [];

  public ngOnInit(): void
  {
    const role = this.authService.getCurrentRole();
    this.isSystemAdmin = role === 'system_admin';
    this.bindClientSearch();
    this.canOpenClient =
      role === 'system_admin' ||
      role === 'company_admin' ||
      role === 'manager';
    if (this.isSystemAdmin) {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.pipe(distinctUntilChanged()).subscribe(() => {
          this.clearFilterOptions();
          this.load();
        }),
      );
      return;
    }

    this.load();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected select(item: RescheduleProposalItem): void
  {
    this.selectedItem = item;
  }

  protected openClient(clientId: string): void
  {
    if (!this.canOpenClient || !clientId.trim()) {
      return;
    }

    this.selectedClientProfileId = clientId.trim();
    this.isClientProfileModalOpen = true;
  }

  protected closeClientProfileModal(): void
  {
    this.isClientProfileModalOpen = false;
    this.selectedClientProfileId = null;
  }

  protected openAppointmentQuickView(appointmentId: string): void
  {
    const normalized = appointmentId.trim();
    if (!normalized) {
      return;
    }

    this.selectedAppointmentId = normalized;
    this.isAppointmentQuickViewOpen = true;
  }

  protected closeAppointmentQuickView(): void
  {
    this.isAppointmentQuickViewOpen = false;
    this.selectedAppointmentId = null;
  }

  protected loadMore(): void
  {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    this.load({ append: true });
  }

  protected retry(): void
  {
    this.load({ append: false });
  }

  protected refresh(): void
  {
    this.load({ append: false });
  }

  private load(options: { append: boolean } = { append: false }): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.selectedItem = null;
      this.nextCursor = null;
      this.hasMoreItems = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      this.actionErrorText = null;
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    if (options.append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
      this.actionErrorText = null;
      this.nextCursor = null;
      this.hasMoreItems = false;
    }

    this.service.getPage({
      companyId: this.getContextCompanyId() ?? undefined,
      limit: RescheduleProposalsPageComponent.PAGE_SIZE,
      cursor: options.append ? this.nextCursor : null,
      sort: this.selectedSort,
      pendingFirst: this.selectedPendingFirst,
      filters: {
        clinicId: this.selectedClinicId || undefined,
        clientId: this.selectedClientId || undefined,
        specialistUserId: this.selectedSpecialistUserId || undefined,
        procedureTypeId: this.selectedProcedureTypeId || undefined,
        deviceId: this.selectedDeviceId || undefined,
        status: this.selectedStatus || undefined,
        reason: this.selectedReason || undefined,
      },
    }).subscribe({
      next: (response: RescheduleProposalPage) =>
      {
        const proposals = response.items;
        this.items = options.append ? [...this.items, ...proposals] : proposals;
        this.nextCursor = response.nextCursor;
        this.hasMoreItems = response.hasMore;

        if (!this.selectedItem && this.items.length > 0)
        {
          this.selectedItem = this.items[0];
        }
        else if (this.selectedItem)
        {
          const refreshedSelectedItem = this.items.find(
            (item) => item.id === this.selectedItem?.id
          );

          this.selectedItem = refreshedSelectedItem ?? (this.items[0] ?? null);
        }

        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (err) =>
      {
        console.error(err);
        if (options.append) {
          this.actionErrorText = resolveHttpErrorMessageKey(err, {
            forbidden: 'error.forbiddenRescheduleProposalsList',
            fallback: 'Failed to load proposals'
          });
          this.isLoadingMore = false;
          return;
        }

        this.errorText = resolveHttpErrorMessageKey(err, {
          forbidden: 'error.forbiddenRescheduleProposalsList',
          fallback: 'Failed to load proposals'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  protected accept(item: RescheduleProposalItem): void
  {
    this.busyId = item.id;
    this.actionErrorText = null;

    this.service.accept(item.id).subscribe({
      next: () =>
      {
        this.busyId = null;
        this.load();
      },
      error: (err) =>
      {
        console.error(err);
        this.busyId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(err, {
          forbidden: 'error.forbiddenRescheduleProposalAction',
          conflictByErrorCode: {
            appointment_client_time_overlap: 'error.appointmentClientTimeOverlap'
          },
          fallback: 'error.rescheduleProposalActionFailed'
        });
      }
    });
  }

  protected reject(item: RescheduleProposalItem): void
  {
    this.busyId = item.id;
    this.actionErrorText = null;

    this.service.reject(item.id).subscribe({
      next: () =>
      {
        this.busyId = null;
        this.load();
      },
      error: (err) =>
      {
        console.error(err);
        this.busyId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(err, {
          forbidden: 'error.forbiddenRescheduleProposalAction',
          fallback: 'error.rescheduleProposalActionFailed'
        });
      }
    });
  }

  protected openFiltersModal(): void
  {
    this.draftClinicId = this.selectedClinicId;
    this.draftClientId = this.selectedClientId;
    this.draftSpecialistUserId = this.selectedSpecialistUserId;
    this.draftProcedureTypeId = this.selectedProcedureTypeId;
    this.draftDeviceId = this.selectedDeviceId;
    this.draftStatus = this.selectedStatus;
    this.draftReason = this.selectedReason;
    this.draftSort = this.selectedSort;
    this.draftPendingFirst = this.selectedPendingFirst;
    this.syncClientSearchText(this.draftClientId);
    this.isFiltersModalOpen = true;
    this.loadFilterOptionsIfNeeded();
  }

  protected closeFiltersModal(): void
  {
    this.isFiltersModalOpen = false;
  }

  protected applyFilters(): void
  {
    this.selectedClinicId = this.draftClinicId;
    this.selectedClientId = this.draftClientId;
    this.selectedSpecialistUserId = this.draftSpecialistUserId;
    this.selectedProcedureTypeId = this.draftProcedureTypeId;
    this.selectedDeviceId = this.draftDeviceId;
    this.selectedStatus = this.draftStatus;
    this.selectedReason = this.draftReason.trim();
    this.selectedSort = this.draftSort;
    this.selectedPendingFirst = this.draftPendingFirst;
    this.closeFiltersModal();
    this.load({ append: false });
  }

  protected resetFilters(): void
  {
    this.draftClinicId = '';
    this.draftClientId = '';
    this.draftClientSearchText = '';
    this.draftSpecialistUserId = '';
    this.draftProcedureTypeId = '';
    this.draftDeviceId = '';
    this.draftStatus = '';
    this.draftReason = '';
    this.draftSort = 'createdAtDesc';
    this.draftPendingFirst = true;
    this.clientOptions = [];
    this.applyFilters();
  }

  protected onDraftClinicChange(event: Event): void
  {
    this.draftClinicId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftSpecialistChange(event: Event): void
  {
    this.draftSpecialistUserId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftProcedureTypeChange(event: Event): void
  {
    this.draftProcedureTypeId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftDeviceChange(event: Event): void
  {
    this.draftDeviceId = (event.target as HTMLSelectElement | null)?.value ?? '';
  }

  protected onDraftStatusChange(event: Event): void
  {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.draftStatus = this.isProposalStatus(value) ? value : '';
  }

  protected onDraftReasonChange(event: Event): void
  {
    this.draftReason = (event.target as HTMLInputElement | null)?.value ?? '';
  }

  protected onDraftSortChange(event: Event): void
  {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.draftSort = this.isProposalSort(value) ? value : 'createdAtDesc';
  }

  protected onDraftPendingFirstChange(event: Event): void
  {
    this.draftPendingFirst = (event.target as HTMLInputElement | null)?.checked ?? true;
  }

  protected onClientSearchInput(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.draftClientSearchText = value;

    if (!value.trim()) {
      this.draftClientId = '';
      this.clientOptions = [];
      return;
    }

    this.clientSearchTerms.next(value);
  }

  protected onClientSearchCommitted(event: Event): void
  {
    const value = (event.target as HTMLInputElement | null)?.value?.trim() ?? '';
    const matchedClient = this.clientOptions.find((client) =>
      this.clientOptionLabel(client) === value || client.id === value,
    );

    if (matchedClient) {
      this.draftClientId = matchedClient.id;
      this.draftClientSearchText = this.clientOptionLabel(matchedClient);
      return;
    }

    this.draftClientId = this.isUuidLike(value) ? value : '';
  }

  protected clientOptionLabel(client: ClientAdminItem): string
  {
    const parts = [client.fullName, client.email, client.phone]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0);

    return parts.length > 0 ? parts.join(' · ') : client.id;
  }

  protected deviceLabel(device: DeviceItem): string
  {
    return [device.brand, device.model, device.serialNumber]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0)
      .join(' · ') || device.id;
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }

  private loadFilterOptionsIfNeeded(): void
  {
    const companyId = this.getContextCompanyId();
    const cacheKey = companyId ?? 'current-company';
    if (this.filterOptionsLoadedForCompanyId === cacheKey || !this.hasRequiredCompanyContext()) {
      return;
    }

    forkJoin({
      clinics: this.settingsService.getClinicOperationalSettings(companyId ?? undefined).pipe(
        catchError(() => of([] as ClinicOperationalSettingsDto[])),
      ),
      devices: this.devicesService.getList(companyId ?? undefined).pipe(
        catchError(() => of([] as DeviceItem[])),
      ),
      procedureTypes: this.procedureTypesService.getList({
        includeInactive: true,
        companyId: companyId ?? undefined,
      }).pipe(
        catchError(() => of([] as ProcedureTypeItem[])),
      ),
      users: this.usersService.getList(companyId ?? undefined).pipe(
        catchError(() => of([] as UserItem[])),
      ),
    }).subscribe(({ clinics, devices, procedureTypes, users }) => {
      this.clinicOptions = clinics;
      this.deviceOptions = devices;
      this.procedureTypeOptions = procedureTypes;
      this.specialistOptions = users.filter((user) => user.role === 'specialist');
      this.filterOptionsLoadedForCompanyId = cacheKey;
    });
  }

  private bindClientSearch(): void
  {
    this.subscriptions.add(
      this.clientSearchTerms.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = term.trim();
          if (query.length < 2 || !this.hasRequiredCompanyContext()) {
            return of([] as ClientAdminItem[]);
          }

          return this.clientsAdminService.getPage({
            companyId: this.getContextCompanyId() ?? undefined,
            limit: RescheduleProposalsPageComponent.CLIENT_SEARCH_LIMIT,
            query,
          }).pipe(
            switchMap((page) => of(page.items)),
            catchError(() => of([] as ClientAdminItem[])),
          );
        }),
      ).subscribe((items) => {
        this.clientOptions = items;
      }),
    );
  }

  private syncClientSearchText(clientId: string): void
  {
    if (!clientId) {
      this.draftClientSearchText = '';
      return;
    }

    const existing = this.clientOptions.find((client) => client.id === clientId);
    if (existing) {
      this.draftClientSearchText = this.clientOptionLabel(existing);
      return;
    }

    this.draftClientSearchText = clientId;
    this.clientsAdminService.getById(
      clientId,
      this.getContextCompanyId() ?? undefined,
    ).subscribe({
      next: (client) => {
        if (this.draftClientId !== client.id) {
          return;
        }

        this.clientOptions = [
          client,
          ...this.clientOptions.filter((item) => item.id !== client.id),
        ];
        this.draftClientSearchText = this.clientOptionLabel(client);
      },
      error: () => {},
    });
  }

  private clearFilterOptions(): void
  {
    this.clinicOptions = [];
    this.clientOptions = [];
    this.deviceOptions = [];
    this.procedureTypeOptions = [];
    this.specialistOptions = [];
    this.filterOptionsLoadedForCompanyId = null;
  }

  private getContextCompanyId(): string | null
  {
    return this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : null;
  }

  private isProposalStatus(value: string): value is RescheduleProposalStatus
  {
    return [
      'pending_client_confirmation',
      'accepted',
      'rejected',
      'expired',
      'revoked',
    ].includes(value);
  }

  private isProposalSort(value: string): value is RescheduleProposalSort
  {
    return [
      'createdAtDesc',
      'createdAtAsc',
      'updatedAtDesc',
      'updatedAtAsc',
      'newStartAtAsc',
      'newStartAtDesc',
    ].includes(value);
  }

  private isUuidLike(value: string): boolean
  {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  }
}
